/**
 * Shared lookup widgets — used by the CrudPage `Field` renderer AND by any
 * hand-rolled subform that needs the same Popover-based searchable picker UX:
 *
 *   • LookupField      — single-select. Trigger shows the picked label or a
 *     placeholder; popover lists every option with a search box; picking a
 *     row closes the popover.
 *
 *   • MultiLookupField — multi-select. Trigger shows selected items as
 *     removable pills; popover stays open so the user can keep picking;
 *     already-picked items are hidden from the dropdown to reduce noise.
 *
 * The visible labels come from the resource's `labelKey` property (e.g.
 * `fleetNumber` for Asset, `fullName` for Employee). Both widgets fetch up
 * to 200 rows from the endpoint and warn when more exist — the search input
 * is the escape hatch for big lists.
 */
import React from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { ChevronsUpDown, Check, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePickerKeyboardScroll } from '@/hooks/use-picker-keyboard-scroll';
import { cn } from '@/lib/utils';

/** Pull an id string out of a value that may already be a string OR a
 *  populated object with `id` / `_id`. Null/undefined → null. */
export function refToId(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    return (obj.id as string) || (obj._id as string) || null;
  }
  return null;
}

/** Array form of refToId. Non-array inputs → []. */
export function refsToIds(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map(refToId).filter((x): x is string => !!x);
}

/** Dot-path getter — `get({a: {b: 1}}, 'a.b')` → 1. */
function dotGet(obj: any, path: string): any {
  if (obj == null) return undefined;
  if (!path) return obj;
  const parts = path.split('.');
  let cur: any = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

const NONE_VALUE = '__null__';

interface LookupFieldProps {
  endpoint: string;
  /** Property name (or dot-path) on each ref row that holds the display
   *  label — e.g. `fleetNumber` for Asset, `fullName` for Employee. */
  labelKey: string;
  /** Current value: a string id, a populated object, or null. */
  value: unknown;
  onChange: (id: string | null) => void;
  /** Extra query params merged into the ref list URL. */
  filter?: Record<string, string>;
  disabled?: boolean;
  /** Placeholder text shown when no selection has been made. */
  placeholder?: string;
  /** Text shown in the trigger when disabled — used by cascading refs
   *  ("Pick a parent first") instead of the regular placeholder. */
  disabledReason?: string;
  /** When true, the "— None —" clear option is hidden — useful for
   *  required fields where empty is invalid. */
  required?: boolean;
  /** Extra classes for the trigger button. Needed where this field sits beside
   *  plain Inputs: the trigger is a Button (size default = h-9) while mobile
   *  form Inputs are h-11, so without an override the two render 8px apart in
   *  height and read as mismatched controls. */
  className?: string;
  /** When true: if a `dependsOn`-driven fetch returns exactly one
   *  matching item and the field is currently empty, auto-pick that
   *  item. Skips the obvious "click the only choice" step. Won't
   *  re-fire if the user explicitly clears the value (we remember
   *  per-filter-context). */
  autoSelectIfSingle?: boolean;
  /** Optional second-line label shown muted underneath the primary
   *  label in each row of the popover (the trigger button still
   *  shows the primary label alone). Use for resources where one
   *  field is a code and another is a human description — e.g.
   *  Catalogue items (itemCode + description). */
  secondaryLabelKey?: string;
  /** When true the server-side search isn't narrowed to `labelKey`
   *  — it spans every configured `searchFields` for the resource.
   *  Pair with `secondaryLabelKey` when the second visible field is
   *  also worth searching against (typing in the description box
   *  surfaces matches even though only the itemCode shows up top). */
  searchAllFields?: boolean;
  /** Fires with the full picked row alongside `onChange(id)`. Lets a
   *  caller stash extra fields off the picked item (e.g. itemCode +
   *  description) without re-fetching by id. Not called on clear. */
  onPick?: (item: any) => void;
  /** Build the visible label from the whole row instead of reading
   *  `labelKey` (e.g. `${code}-${name}`). Implies `searchAllFields`, since
   *  the label shows more than one field. */
  formatLabel?: (item: any) => string;
  /** Load the list in pages of this size, fetching the next page as the
   *  user scrolls to the bottom, instead of one capped 200-row request. */
  pageSize?: number;
}

export function LookupField({
  endpoint, labelKey, value, onChange,
  filter, disabled, placeholder, disabledReason, required, autoSelectIfSingle,
  secondaryLabelKey, searchAllFields, onPick, className, formatLabel, pageSize,
}: LookupFieldProps) {
  const [open, setOpen] = React.useState(false);
  // Measured by usePickerKeyboardScroll: the trigger anchors the geometry (and is
  // where the walk for the field's label starts), the content is what gets capped.
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  usePickerKeyboardScroll(open, triggerRef, listRef);
  const [search, setSearch] = React.useState('');
  // Debounce the search so each keystroke doesn't fire a request. 200ms
  // feels instant to the user and lets the typing burst end first.
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side search: send `?search=` to the endpoint when the user
  // types, so 1000+ row lookups still find their match instead of being
  // hidden behind the 200-row default page. When the search is empty we
  // fall back to the original 200-row alphabetical page.
  const params = new URLSearchParams({ limit: pageSize ? String(pageSize) : (debouncedSearch ? '50' : '200') });
  if (filter) for (const [k, v] of Object.entries(filter)) if (v) params.set(k, v);
  if (debouncedSearch) {
    params.set('search', debouncedSearch);
    // Narrow the server-side regex to just the column we display. Without
    // this, the server searches across every configured searchField
    // (Asset → fleetNumber + make + model + …) and surfaces records whose
    // displayed label doesn't contain the query — confusing UX. Skip for
    // dot-path labelKeys (e.g. `branch.name`) since the server only
    // searches top-level fields. Callers with a `secondaryLabelKey`
    // (catalogue items: itemCode + description) can opt out via
    // `searchAllFields` so typing a description word still surfaces the
    // row, since the secondary line is visible too.
    if (!searchAllFields && !formatLabel && labelKey && !labelKey.includes('.')) {
      params.set('searchField', labelKey);
    }
  }
  const url = `${endpoint}?${params.toString()}`;
  const currentId = refToId(value);
  // Disabled + value still needs a fetch — otherwise the trigger
  // shows the raw ObjectId because the label cache never populates
  // (e.g. wizard tasks where the viewer isn't the assigned
  // responsiblePerson but the field already has an answer).
  const fetchEnabled = !disabled || !!currentId;
  const single = useQuery({
    queryKey: ['ref', url],
    queryFn: async () => {
      const r = await api<any[]>(url);
      return r.data || [];
    },
    enabled: fetchEnabled && !pageSize,
  });
  const paged = useInfiniteQuery({
    queryKey: ['ref-paged', url],
    queryFn: async ({ pageParam }) => api<any[]>(`${url}&page=${pageParam}`),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta && last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
    enabled: fetchEnabled && !!pageSize,
  });
  const items: any[] = React.useMemo(
    () => (pageSize ? (paged.data?.pages ?? []).flatMap((p) => p.data || []) : (single.data || [])),
    [pageSize, paged.data, single.data],
  );
  const isFetching = pageSize ? paged.isFetching : single.isFetching;
  const labelFor = (item: any) => (formatLabel
    ? formatLabel(item)
    : String(dotGet(item, labelKey) || item.name || item.id));
  // Paged mode: pull the next page when the list is scrolled near its end.
  function onListScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!pageSize || !paged.hasNextPage || paged.isFetchingNextPage) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) void paged.fetchNextPage();
  }

  // Auto-select-if-single: when the cascaded fetch returns EXACTLY one
  // candidate and the field is empty, fire onChange with it. Memoised
  // per filter-context so the user can clear and have it stay cleared —
  // we only auto-fire ONCE per unique (filter, endpoint) tuple.
  const autoSelectKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!autoSelectIfSingle || disabled) return;
    if (isFetching) return;
    // Don't auto-select if there's no constraining filter — otherwise a
    // resource that just happens to have 1 row globally would auto-pick
    // for every form, which isn't what the flag is for.
    if (!filter || Object.keys(filter).length === 0) return;
    if (items.length !== 1) return;
    if (currentId) return;
    const ctxKey = JSON.stringify({ endpoint, filter });
    if (autoSelectKeyRef.current === ctxKey) return;
    autoSelectKeyRef.current = ctxKey;
    onChange(String(items[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, currentId, isFetching, autoSelectIfSingle, disabled]);

  // Label cache: remember the label of every item the user has picked in
  // this session. Otherwise picking a row found via server-side search
  // would render as a raw id once the dropdown closes (the next fetch
  // for the default page won't include that item, and `value` is now
  // just an id string so the populated-object fallback doesn't help).
  const labelCacheRef = React.useRef<Map<string, string>>(new Map());
  // Seed from the initial populated value (edit-load case).
  if (value && typeof value === 'object') {
    const vid = refToId(value);
    if (vid && !labelCacheRef.current.has(vid)) {
      const lbl = dotGet(value, labelKey) || (value as any).name;
      if (typeof lbl === 'string' && lbl) labelCacheRef.current.set(vid, lbl);
    }
  }
  // Refresh from items (whatever's in the current page).
  for (const it of items) {
    if (it?.id) labelCacheRef.current.set(String(it.id), labelFor(it));
  }

  const currentLabel = currentId
    ? (labelCacheRef.current.get(currentId) ?? currentId)
    : null;

  // Defense-in-depth: even with `searchField` narrowing the server-side
  // query, drop any row whose visible label doesn't contain the typed
  // text. Covers virtual labelKeys (e.g. Employee `fullName`) that
  // aren't in the server's searchFields whitelist and fall back to
  // multi-field search.
  const filtered = React.useMemo(() => {
    if (!debouncedSearch) return items;
    const needle = debouncedSearch.toLowerCase();
    return items.filter((it) => labelFor(it).toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, debouncedSearch, labelKey]);

  function pick(id: string | null) {
    if (id) {
      const item = items.find((it) => it.id === id);
      if (item) {
        labelCacheRef.current.set(id, labelFor(item));
        onPick?.(item);
      }
    }
    onChange(id);
    setOpen(false);
    setSearch('');
    setDebouncedSearch('');
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSearch(''); setDebouncedSearch(''); } }} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !currentLabel && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">
            {/* Default disabled message was "Pick a parent first" — which only
                made sense for cascading dropdowns. Most disabled cases now
                are permission-driven (e.g. wizard tasks where the user isn't
                the assigned responsiblePerson), so the generic "Not editable"
                fits better. Cascading callers still pass an explicit
                disabledReason ("Pick {parent} first"). */}
            {currentLabel ?? (disabled ? (disabledReason || '— Not editable —') : (placeholder || '— Select —'))}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={listRef}
        align="start"
        // Always downward, never flipped. Radix flips when there's no room below,
        // but `usePickerKeyboardScroll`'s geometry always measures the list as
        // hanging BELOW the trigger — a flipped list occupies space the scroll
        // math still tries to clear, so the form gets pushed up for a list that
        // was never down there. The height cap absorbs a short screen instead.
        // (This is also why the dropdown seemed to open "either upwards or
        // downwards": Radix decided per-open, and the keyboard changed the answer
        // mid-gesture.)
        side="bottom"
        avoidCollisions={false}
        // Cap against the room the viewport actually leaves, not just the list's
        // own 16rem: with the soft keyboard up a phone has ~340px of viewport, so
        // a search row plus a 256px list hangs off the bottom of the screen.
        // Radix measures trigger-to-collision-boundary and publishes it as
        // `--radix-popover-content-available-height` (the fallback reproduces the
        // old behaviour if the var is ever absent). `flex-col` + a `min-h-0`
        // scroll area is what lets the list, rather than the search row, absorb
        // the squeeze. The hook tightens it further against the POST-scroll
        // position, which Radix can't know about.
        className="flex flex-col p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px] max-h-[min(20rem,var(--radix-popover-content-available-height,20rem))]"
      >
        <div className="flex shrink-0 items-center border-b px-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1" onScroll={onListScroll}>
          {!required && (
            <button
              type="button"
              onClick={() => pick(null)}
              className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <span>— None —</span>
              {currentId === null && <Check className="h-4 w-4" />}
            </button>
          )}
          {filtered.length === 0 && debouncedSearch && !isFetching && (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">No matches.</div>
          )}
          {isFetching && (
            <div className="px-2 py-2 text-center text-xs text-muted-foreground">Searching…</div>
          )}
          {filtered.map((item) => {
            const id = String(item.id);
            const label = labelFor(item);
            const secondary = secondaryLabelKey ? dotGet(item, secondaryLabelKey) : null;
            const selected = id === currentId;
            return (
              <button
                key={id}
                type="button"
                onClick={() => pick(id)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                  selected && 'bg-accent',
                )}
              >
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate">{label}</span>
                  {secondary && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {String(secondary)}
                    </span>
                  )}
                </span>
                {selected && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
          {/* Hint only when we're showing the default page (no search) AND it's
              full — typing into the search box fetches server-side. */}
          {!debouncedSearch && items.length >= 200 && (
            <div className="border-t mt-1 px-2 py-2 text-[11px] text-muted-foreground italic">
              Showing first 200 alphabetically — type to search the rest of the list.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface MultiLookupFieldProps {
  endpoint: string;
  labelKey: string;
  value: unknown;
  onChange: (ids: string[]) => void;
  filter?: Record<string, string>;
  disabled?: boolean;
}

export function MultiLookupField({
  endpoint, labelKey, value, onChange, filter, disabled,
}: MultiLookupFieldProps) {
  const [open, setOpen] = React.useState(false);
  // Same keyboard-aware scroll as LookupField — see the note there.
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  usePickerKeyboardScroll(open, triggerRef, listRef);
  const [search, setSearch] = React.useState('');
  // Same debounced server-side search as LookupField — typing into the
  // box queries the endpoint so giant tables (Assets, Catalogue Items)
  // can be searched beyond the default 200-row page.
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const params = new URLSearchParams({ limit: debouncedSearch ? '50' : '200' });
  if (filter) for (const [k, v] of Object.entries(filter)) if (v) params.set(k, v);
  if (debouncedSearch) {
    params.set('search', debouncedSearch);
    // Narrow server-side regex to the displayed column (same reasoning
    // as LookupField — see comment there). Skipped for dot-path keys.
    if (labelKey && !labelKey.includes('.')) params.set('searchField', labelKey);
  }
  const url = `${endpoint}?${params.toString()}`;
  const { data, isFetching } = useQuery({
    queryKey: ['ref', url],
    queryFn: async () => {
      const r = await api<any[]>(url);
      return r.data || [];
    },
    enabled: !disabled,
  });
  const selectedIds = refsToIds(value);
  const items: any[] = data || [];
  const labelFor = (item: any) => String(dotGet(item, labelKey) || item.name || item.id);

  // Same cache pattern as LookupField — once an item has been seen
  // (either as a populated initial value, in the current page, or via
  // a `toggle()` selection), remember its label so the pill renders
  // correctly even after the dropdown refetches the default page.
  const labelCacheRef = React.useRef<Map<string, string>>(new Map());
  for (const it of items) {
    if (it?.id) labelCacheRef.current.set(String(it.id), labelFor(it));
  }
  if (Array.isArray(value)) {
    for (const v of value as any[]) {
      const id = refToId(v);
      if (id && !labelCacheRef.current.has(id) && typeof v === 'object') {
        const lbl = dotGet(v, labelKey) || (v as any).name;
        if (typeof lbl === 'string' && lbl) labelCacheRef.current.set(id, lbl);
      }
    }
  }
  const labelById = labelCacheRef.current;

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      // Stamp the picked item's label so the pill stays readable after
      // the next fetch clears the search and reloads the default page.
      const item = items.find((it) => it.id === id);
      if (item) labelCacheRef.current.set(id, labelFor(item));
      onChange([...selectedIds, id]);
    }
  }
  function remove(id: string) {
    onChange(selectedIds.filter((x) => x !== id));
  }

  const selectedSet = new Set(selectedIds);
  // Same two-pass filter as LookupField: drop already-picked rows AND
  // drop any row whose visible label doesn't contain the typed text
  // (defense-in-depth against virtual labelKeys / non-whitelisted
  // server searchField fallbacks).
  const filtered = React.useMemo(() => {
    const base = items.filter((it) => !selectedSet.has(String(it.id)));
    if (!debouncedSearch) return base;
    const needle = debouncedSearch.toLowerCase();
    return base.filter((it) => labelFor(it).toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, debouncedSearch, labelKey, selectedIds.join(',')]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSearch(''); setDebouncedSearch(''); } }} modal>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex w-full min-h-9 items-start justify-between gap-2 rounded-md border bg-background px-2 py-1.5 text-sm shadow-sm',
            'ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <div className="flex-1 flex flex-wrap gap-1 items-center">
            {selectedIds.length === 0 && (
              <span className="text-muted-foreground py-0.5">— Select —</span>
            )}
            {selectedIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs border"
              >
                {labelById.get(id) || id}
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${labelById.get(id) || id}`}
                  className="text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(id); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); e.stopPropagation(); remove(id);
                    }
                  }}
                >
                  ×
                </span>
              </span>
            ))}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 mt-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={listRef}
        align="start"
        // Always downward, never flipped. Radix flips when there's no room below,
        // but `usePickerKeyboardScroll`'s geometry always measures the list as
        // hanging BELOW the trigger — a flipped list occupies space the scroll
        // math still tries to clear, so the form gets pushed up for a list that
        // was never down there. The height cap absorbs a short screen instead.
        // (This is also why the dropdown seemed to open "either upwards or
        // downwards": Radix decided per-open, and the keyboard changed the answer
        // mid-gesture.)
        side="bottom"
        avoidCollisions={false}
        // Cap against the room the viewport actually leaves, not just the list's
        // own 16rem: with the soft keyboard up a phone has ~340px of viewport, so
        // a search row plus a 256px list hangs off the bottom of the screen.
        // Radix measures trigger-to-collision-boundary and publishes it as
        // `--radix-popover-content-available-height` (the fallback reproduces the
        // old behaviour if the var is ever absent). `flex-col` + a `min-h-0`
        // scroll area is what lets the list, rather than the search row, absorb
        // the squeeze. The hook tightens it further against the POST-scroll
        // position, which Radix can't know about.
        className="flex flex-col p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px] max-h-[min(20rem,var(--radix-popover-content-available-height,20rem))]"
      >
        <div className="flex shrink-0 items-center border-b px-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {filtered.length === 0 && debouncedSearch && !isFetching && (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">No matches.</div>
          )}
          {filtered.length === 0 && !debouncedSearch && items.length > 0 && !isFetching && (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">All items selected.</div>
          )}
          {filtered.length === 0 && !debouncedSearch && items.length === 0 && !isFetching && (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">No options.</div>
          )}
          {isFetching && (
            <div className="px-2 py-2 text-center text-xs text-muted-foreground">Searching…</div>
          )}
          {filtered.map((item) => {
            const id = String(item.id);
            const label = labelFor(item);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggle(id)}
                className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <span className="truncate">{label}</span>
              </button>
            );
          })}
          {!debouncedSearch && items.length >= 200 && (
            <div className="border-t mt-1 px-2 py-2 text-[11px] text-muted-foreground italic">
              Showing first 200 alphabetically — type to search the rest of the list.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
